// Sugestão do teto mensal de compras a partir da aba Custo de Mercadoria.
//
// Âncora "meta de custo": teto sugerido = média mensal de (faturamento × meta%)
// dos últimos meses COMPLETOS (o "Meta em R$" da Custo de Mercadoria). Gastar no
// máximo a meta de custo mantém o índice de custo na meta. Os dados são por
// empresa (base matriz, RLS) e já descontam filial no costReal. Puro/determinístico.

import type { CostOfGoodsEntry } from '../types'

export const DEFAULT_TARGET_PCT = 55

export interface CapSuggestion {
  /** Teto sugerido (R$, arredondado a R$100); null se não há mês completo com dado. */
  value: number | null
  /** Quantos meses entraram na média. */
  monthsUsed: number
}

/**
 * @param entries linhas da Custo de Mercadoria (ordenadas asc por ano/mês).
 * @param opts.months janela de meses completos (default 6).
 * @param opts.now data de referência (default hoje) — define o mês corrente a excluir.
 * @param opts.defaultTargetPct meta de custo (%) da empresa usada quando a linha não
 *   tem `target_pct` próprio (default 55).
 */
export function suggestMonthlyCap(
  entries: ReadonlyArray<CostOfGoodsEntry>,
  opts: { months?: number; now?: Date; defaultTargetPct?: number } = {},
): CapSuggestion {
  const months = opts.months ?? 6
  const defaultTargetPct = opts.defaultTargetPct ?? DEFAULT_TARGET_PCT
  const now = opts.now ?? new Date()
  const curY = now.getFullYear()
  const curM = now.getMonth() + 1

  // Só meses passados COMPLETOS e com faturamento informado.
  const completos = entries.filter(e => {
    if ((e.revenue_gross ?? 0) <= 0) return false
    const passado = e.period_year < curY || (e.period_year === curY && e.period_month < curM)
    return passado
  })
  const janela = completos.slice(-months)
  if (janela.length === 0) return { value: null, monthsUsed: 0 }

  const soma = janela.reduce(
    (s, e) => s + e.revenue_gross * ((e.target_pct ?? defaultTargetPct) / 100),
    0,
  )
  const media = soma / janela.length
  return { value: Math.round(media / 100) * 100, monthsUsed: janela.length }
}
